import React from "react";
import {
  Navbar,
  NavbarBrand,
  NavbarContent,
  NavbarItem,
  NavbarMenu,
  NavbarMenuItem,
  NavbarMenuToggle,
  Link,
  Button,
  Divider,
  cn,
} from "@heroui/react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, Moon, Sun } from "lucide-react";


const navItems: Array<{ key: string; href: string; label: string }> = [
  { key: "home", href: "https://www.3chatai.com/", label: "首页" },
  // { key: "station", href: "/station", label: "3Chat Station" },
  // { key: "product", href: "/solutions/product/product_omnichannel", label: "产品" },
  // { key: "features", href: "#features", label: "卖点" },
  // { key: "solution", href: "/solutions/science-demo", label: "解决方案" },
  // { key: "livechat", href: "/livechat/dtc-ecommerce", label: "体验中心" },
  { key: "pricing", href: "#pricing", label: "价格" },
  // { key: "skillStore", href: "/skillStore", label: "技能市场" },
  { key: "resources", href: "https://group.3chatai.cn/", label: "资源" },
  { key: "faq", href: "#faq", label: "常见问题" },
];

const BasicNavbar = React.forwardRef<
  HTMLElement,
  React.ComponentProps<typeof Navbar>
>(({ classNames = {}, ...props }, ref) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const initialKey =
    typeof window !== "undefined"
      ? window.location.pathname.startsWith("/skillStore")
        ? "skillStore"
        : window.location.pathname.startsWith("/3chatclaw")
          ? "station"
          : "home"
      : "home";
  const [selectedKey, setSelectedKey] = React.useState(initialKey);
  const [isProductOpen, setIsProductOpen] = React.useState(false);
  const [isMobileProductOpen, setIsMobileProductOpen] = React.useState(false);
  const [isResourcesOpen, setIsResourcesOpen] = React.useState(false);
  const [isMobileResourcesOpen, setIsMobileResourcesOpen] = React.useState(false);
  const [isDarkMode, setIsDarkMode] = React.useState(() => {
    if (typeof document === "undefined") return true;
    return document.documentElement.classList.contains("dark");
  });

  const productHoverTimeout = React.useRef<number | null>(null);
  const resourcesHoverTimeout = React.useRef<number | null>(null);

  const openProduct = () => {
    if (productHoverTimeout.current) {
      window.clearTimeout(productHoverTimeout.current);
    }
    setIsProductOpen(true);
  };

  const closeProduct = () => {
    if (productHoverTimeout.current) {
      window.clearTimeout(productHoverTimeout.current);
    }
    productHoverTimeout.current = window.setTimeout(() => {
      setIsProductOpen(false);
    }, 80);
  };

  const openResources = () => {
    if (resourcesHoverTimeout.current) {
      window.clearTimeout(resourcesHoverTimeout.current);
    }
    setIsResourcesOpen(true);
  };

  const closeResources = () => {
    if (resourcesHoverTimeout.current) {
      window.clearTimeout(resourcesHoverTimeout.current);
    }
    resourcesHoverTimeout.current = window.setTimeout(() => {
      setIsResourcesOpen(false);
    }, 80);
  };

  const handleNavClick = (e: React.MouseEvent, key: string, href: string) => {
    setSelectedKey(key);

    // Handle anchor links
    if (href.startsWith("#")) {
      e.preventDefault();
      const id = href.slice(1);

      // If not on home page, navigate to home first, then scroll
      if (location.pathname !== "/") {
        navigate(`/#${id}`);
        // Scroll after navigation
        setTimeout(() => {
          document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      } else {
        // Already on home page, just scroll
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }
      return;
    }

    // For external links, let the browser handle the navigation
    // No need to prevent default behavior
  };

  const toggleTheme = () => {
    const root = document.documentElement;
    const nextIsDark = !root.classList.contains("dark");
    root.classList.toggle("dark", nextIsDark);
    setIsDarkMode(nextIsDark);
  };

  return (
    <Navbar
      ref={ref}
      {...props}
      classNames={{
        base: cn(
          "fixed top-0 left-0 right-0 z-50 border-default-100 backdrop-blur-md backdrop-saturate-150",
          "bg-default-100/10 dark:bg-default-100/10",
          { "bg-default-200/50 dark:bg-default-100/50": isMenuOpen }
        ),
        wrapper: "w-full justify-center",
        item: "hidden md:flex",
        ...classNames,
      }}
      height="60px"
      isMenuOpen={isMenuOpen}
      onMenuOpenChange={setIsMenuOpen}
    >
      <NavbarBrand>
        <div className="rounded-full p-1">
          <img
            src="/images/3Chat.ai_Logo.svg"
            alt="3Chat.ai Logo"
            className="h-8 w-auto"
          />
        </div>

      </NavbarBrand>

      <NavbarContent justify="center">
        {navItems.map((item) =>
          item.key === "product" ? (
            <NavbarItem
              key={`${item.key}-${item.label}`}
              isActive={selectedKey === item.key}
              className="relative data-[active=true]:font-medium"
              onMouseEnter={openProduct}
              onMouseLeave={closeProduct}
            >
              <button
                type="button"
                className={cn(
                  "text-sm outline-none hover:text-default-foreground cursor-pointer",
                  isProductOpen || selectedKey === item.key
                    ? "text-default-foreground"
                    : "text-default-500"
                )}
              >
                {item.label}
              </button>

              <div
                className={cn(
                  "pointer-events-auto absolute left-1/2 top-full z-50 mt-1 -translate-x-1/2 min-w-[200px] rounded-xl border border-default-100 bg-background px-2 py-2 shadow-lg transition-opacity duration-150",
                  isProductOpen ? "opacity-100" : "pointer-events-none opacity-0"
                )}
              >
              <Link
                  className="block rounded-lg px-3 py-2 text-sm text-default-600 hover:bg-default-100 cursor-pointer"
                  href={`${window.location.origin}/solutions/product/product_omnichannel`}
                  onClick={(e) =>
                    handleNavClick(e, item.key, "/solutions/product/product_omnichannel")
                  }
                >
                  3ChatAgent
                </Link>
                <Link
                  className="mt-1 block rounded-lg px-3 py-2 text-sm text-default-600 hover:bg-default-100 hover:text-default-foreground cursor-pointer"
                  href="https://claw.3chatai.com/"
                  isExternal
                  onClick={(e) => handleNavClick(e, item.key, "https://claw.3chatai.com/")}
                >
                  3ChatClaw 🦞
                </Link>
              </div>
            </NavbarItem>
          ) : item.key === "resources" ? (
            <NavbarItem
              key={`${item.key}-${item.label}`}
              isActive={selectedKey === item.key}
              className="relative data-[active=true]:font-medium"
              onMouseEnter={openResources}
              onMouseLeave={closeResources}
            >
              <button
                type="button"
                className={cn(
                  "text-sm outline-none hover:text-default-foreground cursor-pointer",
                  isResourcesOpen || selectedKey === item.key
                    ? "text-default-foreground"
                    : "text-default-500"
                )}
              >
                {item.label}
              </button>

              <div
                className={cn(
                  "pointer-events-auto absolute left-1/2 top-full z-50 mt-1 -translate-x-1/2 min-w-[160px] rounded-xl border border-default-100 bg-background px-2 py-2 shadow-lg transition-opacity duration-150",
                  isResourcesOpen ? "opacity-100" : "pointer-events-none opacity-0"
                )}
              >
                <Link
                  className="block rounded-lg px-3 py-2 text-sm text-default-600 hover:bg-default-100 cursor-pointer"
                  href="https://group.3chatai.cn/"
                  isExternal
                  onClick={(e) =>
                    handleNavClick(e, item.key, "https://group.3chatai.cn/")
                  }
                >
                  社区
                </Link>
                <Link
                  className="mt-1 block rounded-lg px-3 py-2 text-sm text-default-600 hover:bg-default-100 hover:text-default-foreground cursor-pointer"
                  href="https://3chatai.com/blog"
                  onClick={(e) => handleNavClick(e, item.key, "https://3chatai.com/blog")}
                >
                  博客
                </Link>
              </div>
            </NavbarItem>
          ) : (
            <NavbarItem
              key={`${item.key}-${item.label}`}
              isActive={selectedKey === item.key}
              className="data-[active=true]:font-medium"
            >
              <Link
                aria-current={selectedKey === item.key ? "page" : undefined}
                className={cn(
                  "text-sm hover:text-default-foreground cursor-pointer",
                  selectedKey === item.key ? "text-default-foreground" : "text-default-500"
                )}
                href={
                  item.href.startsWith("#")
                    ? item.href
                    : item.href.startsWith("http")
                    ? item.href
                    : `${window.location.origin}${item.href}`
                }
                onClick={(e) => handleNavClick(e, item.key, item.href)}
              >
                {item.label}
              </Link>
            </NavbarItem>
          )
        )}
        {/* 隐藏：带渐变动画的 3ChatClaw 按钮 */}
        {/*
        <NavbarItem className="ml-4 data-[active=true]:font-medium">
          <Link
            href="https://claw.3chatai.com/"
            isExternal
            className="text-sm hover:text-default-foreground cursor-pointer"
          >
            <span className="gradient-shimmer-text">3ChatClaw 🦞</span>
          </Link>
        </NavbarItem>
        */}
      </NavbarContent>

      <NavbarContent className="hidden md:flex" justify="end">
        <NavbarItem className="ml-2 flex gap-2">
          {/*
          <Button
            isIconOnly
            variant="light"
            radius="full"
            className="text-default-500 cursor-pointer"
            aria-label={isDarkMode ? "切换到浅色模式" : "切换到深色模式"}
            onPress={toggleTheme}
          >
            {isDarkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
          */}
          {/* <Button
            as={Link}
            className="text-default-500 cursor-pointer"
            href={`${window.location.origin}/login?utm_source=3chatclaw`}
            radius="full"
            variant="light"
          >
            登录
          </Button>
          <Button
            as={Link}
            className="bg-default-foreground text-background font-medium cursor-pointer"
            color="secondary"
            endContent={<ArrowRight className="w-4 h-4" />}
            href={`${window.location.origin}/signup?utm_source=3chatclaw`}
            radius="full"
            variant="flat"
          >
            免费试用
          </Button> */}
        </NavbarItem>
      </NavbarContent>

      <NavbarMenuToggle className="text-default-400 md:hidden cursor-pointer" />

      <NavbarMenu
        className="dark bg-default-100/10 shadow-medium top-[calc(var(--navbar-height)-1px)] max-h-fit pt-6 pb-6 backdrop-blur-md backdrop-saturate-150"
        motionProps={{
          initial: { opacity: 0, y: -20 },
          animate: { opacity: 1, y: 0 },
          exit: { opacity: 0, y: -20 },
          transition: {
            ease: "easeInOut",
            duration: 0.2,
          },
        }}
      >
        {/*
        <NavbarMenuItem>
          <Button
            fullWidth
            variant="faded"
            startContent={isDarkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            onPress={toggleTheme}
          >
            {isDarkMode ? "切换浅色模式" : "切换深色模式"}
          </Button>
        </NavbarMenuItem>
        */}
        {/* <NavbarMenuItem>
          <Button
            fullWidth
            as={Link}
            href={`${window.location.origin}/login?utm_source=3chatclaw`}
            variant="faded"
          >
            登录
          </Button>
        </NavbarMenuItem> */}
        {/* <NavbarMenuItem className="mb-4">
          <Button
            fullWidth
            as={Link}
            className="bg-foreground text-background cursor-pointer"
            href={`${window.location.origin}/signup?utm_source=3chatclaw`}
          >
            免费试用
          </Button>
        </NavbarMenuItem> */}
        {navItems.map((item, index) =>
          item.key === "product" ? (
            <NavbarMenuItem key={`${item.key}-${item.label}`} className="flex flex-col">
              <button
                type="button"
                className="mb-2 w-full text-left text-md text-default-foreground cursor-pointer"
                onClick={() => setIsMobileProductOpen((prev) => !prev)}
              >
                {item.label}
              </button>
              <div
                className={cn(
                  "mobile-submenu",
                  isMobileProductOpen && "mobile-submenu-open"
                )}
              >
                <Link
                  className="mb-1 w-full text-md text-default-600 cursor-pointer"
                  href={`${window.location.origin}/solutions/product/product_omnichannel`}
                  onClick={(e) => {
                    handleNavClick(e, item.key, "/solutions/product/product_omnichannel");
                    setIsMenuOpen(false);
                    setIsMobileProductOpen(false);
                  }}
                >
                  3Chat.ai
                </Link>
                <Link
                  className="mb-2 w-full text-md text-default-600 cursor-pointer"
                  href="https://claw.3chatai.com/"
                  isExternal
                  onClick={(e) => {
                    handleNavClick(e, item.key, "https://claw.3chatai.com/");
                    setIsMenuOpen(false);
                    setIsMobileProductOpen(false);
                  }}
                >
                  3ChatClaw 🦞
                </Link>
              </div>
              {index < navItems.length - 1 && <Divider className="opacity-50" />}
            </NavbarMenuItem>
          ) : item.key === "resources" ? (
            <NavbarMenuItem key={`${item.key}-${item.label}`} className="flex flex-col">
              <button
                type="button"
                className="mb-2 w-full text-left text-md text-default-foreground cursor-pointer"
                onClick={() => setIsMobileResourcesOpen((prev) => !prev)}
              >
                {item.label}
              </button>
              <div
                className={cn(
                  "mobile-submenu",
                  isMobileResourcesOpen && "mobile-submenu-open"
                )}
              >
                <Link
                  className="mb-1 w-full text-md text-default-600 cursor-pointer"
                  href="https://group.3chatai.cn/"
                  isExternal
                  onClick={(e) => {
                    handleNavClick(e, item.key, "https://group.3chatai.cn/");
                    setIsMenuOpen(false);
                    setIsMobileResourcesOpen(false);
                  }}
                >
                  社区
                </Link>
                <Link
                  className="mb-2 w-full text-md text-default-600 cursor-pointer"
                  href="https://3chatai.com/blog"
                  onClick={(e) => {
                    handleNavClick(e, item.key,"https://3chatai.com/blog");
                    setIsMenuOpen(false);
                    setIsMobileResourcesOpen(false);
                  }}
                >
                  博客
                </Link>
              </div>
              {index < navItems.length - 1 && <Divider className="opacity-50" />}
            </NavbarMenuItem>
          ) : (
            <NavbarMenuItem key={`${item.key}-${item.label}`}>
              <Link
                className="text-default-foreground mb-2 w-full text-md cursor-pointer"
                href={
                  item.href.startsWith("#")
                    ? item.href
                    : item.href.startsWith("http")
                    ? item.href
                    : `${window.location.origin}${item.href}`
                }
                onClick={(e) => {
                  handleNavClick(e, item.key, item.href);
                  setIsMenuOpen(false);
                }}
              >
                {item.label}
              </Link>
              {index < navItems.length - 1 && <Divider className="opacity-50" />}
            </NavbarMenuItem>
          )
        )}
        {/* 隐藏：带渐变动画的 3ChatClaw 按钮 */}
        {/*
        <NavbarMenuItem>
          <Link
            className="mb-2 w-full text-md text-default-foreground cursor-pointer"
            href="https://claw.3chatai.com/"
            isExternal
            onClick={(e) => {
              handleNavClick(e, "claw", "https://claw.3chatai.com/");
              setIsMenuOpen(false);
            }}
          >
            <span className="gradient-shimmer-text">3ChatClaw 🦞</span>
          </Link>
          <Divider className="opacity-50" />
        </NavbarMenuItem>
        */}
      </NavbarMenu>
    </Navbar>
  );
});

BasicNavbar.displayName = "BasicNavbar";

export default BasicNavbar;
